import axios from 'axios';
import { 
  Subject,
  SubjectListParams, 
  SubjectListResponse,
  SubjectCreateRequest, 
  SubjectUpdateRequest,
  SubjectSyncRequest,
  SubjectSyncResponse,
  SubjectImportRequest,
  SubjectImportResponse,
  SubjectLockRequest,
  SubjectLockResponse
} from '../../../shared/types';

const API_BASE = '/api/v1/subjects';

// List subjects with pagination and filtering
export async function listSubjects(params?: SubjectListParams): Promise<SubjectListResponse> {
  const response = await axios.get(API_BASE, { params });
  return response.data;
}

// Get subject by ID
export async function getSubject(id: string): Promise<Subject> {
  const response = await axios.get(`${API_BASE}/${id}`);
  return response.data;
}

// Create new subject
export async function createSubject(data: SubjectCreateRequest): Promise<Subject> {
  const response = await axios.post(API_BASE, data);
  return response.data;
}

// Update subject
export async function updateSubject(id: string, data: SubjectUpdateRequest): Promise<{ message: string }> {
  const response = await axios.put(`${API_BASE}/${id}`, data);
  return response.data;
}

// Delete subject
export async function deleteSubject(id: string): Promise<{ message: string }> {
  const response = await axios.delete(`${API_BASE}/${id}`);
  return response.data;
}

// Sync subjects from secondary database
export async function syncSubjects(data: SubjectSyncRequest): Promise<SubjectSyncResponse> {
  const response = await axios.post(`${API_BASE}/sync`, data);
  return response.data;
}

// Update TongSoBuoi for a subject
export async function updateSubjectTongSoBuoi(id: string, tongSoBuoi: number): Promise<{ success: boolean; message: string; data: any }> {
  const response = await axios.patch(`${API_BASE}/${id}/tongsob`, { TongSoBuoi: tongSoBuoi });
  return response.data;
}

// Import subjects from Excel
export async function importSubjects(data: SubjectImportRequest): Promise<SubjectImportResponse> {
  const response = await axios.post(`${API_BASE}/import`, data);
  return response.data;
}

// Download Excel template for import
export function downloadSubjectTemplate(): void {
  const templateData = [
    ['MonHocID', 'TenMonHoc', 'SoTC', 'HocKy', 'KhoaHocID'],
    ['1760001', 'Tiếng anh cơ bản', 2, 1, '17601'],
    ['1760002', 'Tin học', 3, 1, '17601'],
    ['1760003', 'Chính trị', 2, 1, '17601']
  ];

  // Create CSV content
  const csvContent = templateData
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'subject_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Helper function to parse Excel data for import
export function parseSubjectExcelData(jsonData: any[]): SubjectCreateRequest[] {
  if (!Array.isArray(jsonData) || jsonData.length < 2) {
    throw new Error('Dữ liệu Excel không hợp lệ');
  }

  // Skip header row
  const dataRows = jsonData.slice(1);
  
  return dataRows
    .filter(row => row && row.length >= 5) // Ensure row has enough columns
    .map((row, index) => {
      try {
        return {
          MonHocID: String(row[0]).trim(),
          TenMonHoc: String(row[1]).trim(),
          SoTC: row[2] ? Number(row[2]) : 0,
          HocKy: row[3] ? Number(row[3]) : 1,
          KhoaHocID: String(row[4]).trim()
        };
      } catch (error) {
        throw new Error(`Lỗi dữ liệu tại dòng ${index + 2}: ${error}`);
      }
    })
    .filter(item => item.MonHocID && item.TenMonHoc && item.KhoaHocID); // Filter out invalid rows
}

// Get subjects by course ID
export async function getSubjectsByCourse(courseId: string): Promise<Subject[]> {
  const response = await axios.get(`${API_BASE}/by-khoahoc/${courseId}`);
  // Backend trả về { data: [...], pagination: {...} }
  return response.data.data || response.data;
}

// Lock/Unlock subject attendance
export async function lockSubjectAttendance(id: string, isLocked: boolean): Promise<SubjectLockResponse> {
  const response = await axios.patch(`${API_BASE}/${id}/lock`, { isLocked });
  return response.data;
}

// Convenience functions for lock/unlock
export async function lockAttendance(id: string): Promise<SubjectLockResponse> {
  return lockSubjectAttendance(id, true);
}

export async function unlockAttendance(id: string): Promise<SubjectLockResponse> {
  return lockSubjectAttendance(id, false);
}